import React from 'react';
import { useAvpAtlassianUser } from '@jira/platform__avp-config/src/useAvpAtlassianUser.tsx';
import { useAvpWorkspace } from '@jira/platform__avp-config/src/useAvpWorkspace.tsx';
import { useIsOrgAdmin } from '@jira/platform__services-is-org-admin/src/is-org-admin.tsx';
import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
// Using AnalyticsContext directly to pass source context to AVP components (ContextualAnalyticsData doesn't support custom source prop)
import { fg } from '@atlassian/jira-feature-gating';
import { useAssetsDashboardsSemanticModel } from '@jira/router-resources__viz-platform/src/assetsDashboardsSemanticModelResource.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { AssetsReportsV2Props } from './AssetsReportsV2Props.types.tsx';
import { isNewReportsNavExperienceEnabled } from './isNewReportsNavExperienceEnabled.tsx';

const LazyITSMAnalyticsDashboardDirectory = lazyForPaint(() =>
	import(/* webpackChunkName: "avp-viz-platform-views" */ '@atlassian/viz-platform-views').then(
		({ ITSMAnalyticsDashboardDirectory }) => ITSMAnalyticsDashboardDirectory,
	),
);

export const DashboardList = ({ workspaceId }: AssetsReportsV2Props): React.JSX.Element => {
	const { locale } = useIntl();
	const cloudId = useCloudId();
	const atlassianUser = useAvpAtlassianUser();
	const workspace = useAvpWorkspace(workspaceId);
	const environment = useEnvironment();
	const { isOrgAdmin } = useIsOrgAdmin();

	const dashboardEditableBaseUrl = isNewReportsNavExperienceEnabled()
		? '/jira/assets/dashboards'
		: '/jira/assets/reports';

	const templatePlaceholderReplacementsApiPath = `/gateway/api/jsm/reporting/assets/workspace/${workspaceId}/template-details`;

	const content = (
		<LazyITSMAnalyticsDashboardDirectory
			activationId={workspaceId}
			hostingProduct="jsm-cmdb"
			cloudId={cloudId}
			location="assets"
			dashboardEditableBaseUrl={dashboardEditableBaseUrl}
			services={{
				useSemanticModel: fg('platform_avp_enable_jsm_assets_metrics')
					? useAssetsDashboardsSemanticModel
					: undefined,
			}}
			templatePlaceholderReplacementsApiPath={templatePlaceholderReplacementsApiPath}
			locale={locale}
			atlassianUser={atlassianUser}
			workspace={workspace}
			isUserOrgAdmin={isOrgAdmin}
			environment={environment}
			perimeter="commercial"
		/>
	);

	if (fg('jsm_assets_analytics_context_migration')) {
		return <AnalyticsContext data={{ source: 'assets-reporting' }}>{content}</AnalyticsContext>;
	}

	return content;
};
